<template>
  <div class="login-wrapper">
    <div class="login-form-container">
      <!-- 头部 Tab 切换 -->
      <div class="login-header">
        <div class="custom-tabs">
          <div
            class="tab-item"
            :class="{ active: activeTab === 'password' }"
            @click="activeTab = 'password'"
          >
            邮箱登录
            <!-- 动态下划线 -->
            <span class="active-line"></span>
          </div>
          <div
            class="tab-item"
            :class="{ active: activeTab === 'code' }"
            @click="activeTab = 'code'"
          >
            验证码登录
            <span class="active-line"></span>
          </div>
        </div>
      </div>

      <div class="login-content">
        <!-- 密码登录表单 -->
        <el-form
          v-if="activeTab === 'password'"
          :model="passwordLogin"
          class="custom-form"
          size="large"
        >
          <el-form-item prop="mail">
            <el-input
              v-model="passwordLogin.mail"
              placeholder="请输入邮箱账号"
              :prefix-icon="Message"
              clearable
              class="custom-input"
            />
          </el-form-item>

          <el-form-item prop="password">
            <el-input
              v-model="passwordLogin.password"
              placeholder="请输入登录密码"
              :prefix-icon="Lock"
              show-password
              clearable
              class="custom-input"
              @keyup.enter="handleLogin"
            />
          </el-form-item>

          <div class="form-options">
            <el-checkbox v-model="passwordLogin.remember">记住账号</el-checkbox>
            <!-- <el-link
              type="primary"
              :underline="false"
              @click="handleForgotPassword"
              class="forgot-link"
            >
              忘记密码?
            </el-link> -->
          </div>
        </el-form>

        <!-- 验证码登录表单 -->
        <el-form 
          v-else 
          :model="codeLogin" 
          class="custom-form"
          size="large"
        >
          <el-form-item prop="phone">
            <el-input
              v-model="codeLogin.phone"
              placeholder="请输入手机号码"
              :prefix-icon="Phone"
              clearable
              class="custom-input"
            />
          </el-form-item>

          <el-form-item prop="code">
            <el-input
              v-model="codeLogin.code"
              placeholder="请输入验证码"
              :prefix-icon="ChatLineRound"
              class="custom-input code-input"
              @keyup.enter="handleLogin"
            >
              <template #append>
                <el-button
                  class="code-btn"
                  link
                  :disabled="isCountingDown"
                  @click="sendVerificationCode"
                >
                  {{ countdown > 0 ? `${countdown}s 后重试` : "获取验证码" }}
                </el-button>
              </template>
            </el-input>
          </el-form-item>
        </el-form>

        <!-- 操作按钮组 -->
        <div class="form-buttons">
          <el-button 
            type="primary" 
            class="action-btn login-btn" 
            :loading="loading"
            @click="handleLogin" 
            round
          >
            立即登录
          </el-button>
          <el-button 
            class="action-btn register-btn" 
            @click="emit('onRegister')" 
            round
          >
            注册新账号
          </el-button>
        </div>
      </div>
    </div>

    <!-- 多账号选择弹窗 -->
    <el-dialog
      v-model="showSelectDialog"
      title="请选择登录账号"
      width="400px"
      align-center
      destroy-on-close
      class="account-select-dialog"
    >
      <div class="account-list">
        <div 
          v-for="(account, index) in accountList" 
          :key="index"
          class="account-card"
          :class="{ 'is-selected': selectedAccountIndex === index }"
          @click="selectedAccountIndex = index"
        >
          <div class="card-header">
            <span class="user-name">{{ account.customerName || '未命名用户' }}</span>
            <el-icon v-if="selectedAccountIndex === index" class="check-icon"><Check /></el-icon>
          </div>
          <div class="card-info">
            <p><el-icon><Iphone /></el-icon> {{ account.mobile || account.contactPhone }}</p>
            <p><el-icon><Message /></el-icon> {{ account.mail }}</p>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showSelectDialog = false">取消</el-button>
          <el-button type="primary" @click="handleConfirmSelect" :loading="dialogLoading">
            确认登录
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { Lock, Phone, ChatLineRound, Message, Check, Iphone } from "@element-plus/icons-vue";
import { useUserStore } from "@/stores/user";
import { getLoginCodeApi } from "@/apis/login";
import { refreshStoredUserInfo } from "@/utils/userInfoRefresh";
import router from "@/pc/router";

const props = defineProps({
  redirectOnSuccess: {
    type: Boolean,
    default: true,
  },
  redirectTo: {
    type: [String, Object],
    default: '',
  },
});

const emit = defineEmits(["onRegister", "loginSuccess"]);
const userStore = useUserStore();

// 状态
const activeTab = ref("password");
const loading = ref(false);

const passwordLogin = reactive({
  password: "",
  remember: false,
  mail: ""
});

const codeLogin = reactive({
  phone: "",
  code: "",
});

const countdown = ref(0);
const isCountingDown = ref(false);
let countdownTimer = null;

// 多账号选择相关状态
const showSelectDialog = ref(false);
const accountList = ref([]);
const selectedAccountIndex = ref(-1);
const dialogLoading = ref(false);

// 发送验证码
const sendVerificationCode = async () => {
  if (!codeLogin.phone) {
    ElMessage.warning("请输入手机号");
    return;
  }
  try {
     await getLoginCodeApi(codeLogin.phone);
     ElMessage.success("验证码已发送");
     isCountingDown.value = true;
     countdown.value = 60;
     countdownTimer = setInterval(() => {
       countdown.value--;
       if (countdown.value <= 0) {
         clearInterval(countdownTimer);
         isCountingDown.value = false;
       }
     }, 1000);
  } catch (e) {
    ElMessage.error("发送失败，请稍后重试");
  }
};

// 登录逻辑
const handleLogin = async () => {
  loading.value = true;
  try {
    if (activeTab.value === "password") {
      // --- 密码登录逻辑 ---
      if (!passwordLogin.mail || !passwordLogin.password) {
        ElMessage.warning("请输入邮箱和密码");
        loading.value = false;
        return;
      }
      
      if (passwordLogin.remember) {
        localStorage.setItem("savedAccount", JSON.stringify(passwordLogin));
      } else {
        localStorage.removeItem("savedAccount");
      }

      await userStore.passwordLogin(passwordLogin.mail, passwordLogin.password);
      await loginSuccess();

    } else {
      // --- 验证码登录逻辑 ---
      if (!codeLogin.phone || !codeLogin.code) {
        ElMessage.warning("请输入手机号和验证码");
        loading.value = false;
        return;
      }
      
      const response = await userStore.codeLogin(codeLogin.phone, codeLogin.code);
      
      // 处理 205 状态码：多账号选择
      if (response && response.code === 205) {
        accountList.value = response.data || [];
        if (accountList.value.length > 0) {
          showSelectDialog.value = true; // 打开弹窗
          selectedAccountIndex.value = 0; // 默认选中第一个
        } else {
          ElMessage.warning("未找到关联账号信息");
        }
        return; 
      }
      
      await loginSuccess();
    }
  } catch (err) {
    ElMessage.error(typeof err === 'string' ? err : '登录失败');
  } finally {
    loading.value = false;
  }
};

const refreshFullUserInfo = async () => {
  try {
    return await refreshStoredUserInfo();
  } catch (error) {
    if (!error?.isAuthError) {
      ElMessage.warning("登录成功，账号信息同步失败，请刷新重试");
    }
  }
  return null;
};

// 登录成功后的通用处理
const loginSuccess = async () => {
  ElMessage.success("登录成功");
  await refreshFullUserInfo();
  const redirect = props.redirectTo || router.currentRoute.value.query.redirect || "/";

  emit("loginSuccess");
  if (props.redirectOnSuccess) {
    await router.push(redirect).catch(() => undefined);
  }
}

// 处理弹窗确认选择
const handleConfirmSelect = async () => {
  if (selectedAccountIndex.value === -1) {
    ElMessage.warning("请选择一个账号");
    return;
  }

  const selectedAccount = accountList.value[selectedAccountIndex.value];
  dialogLoading.value = true;

  try {
    await userStore.phoneMailLogin(selectedAccount.mobile, selectedAccount.mail || '');
    
    showSelectDialog.value = false; // 关闭弹窗
    await loginSuccess(); // 跳转
  } catch (err) {
    ElMessage.error(typeof err === 'string' ? err : '登录失败');
  } finally {
    dialogLoading.value = false;
  }
};

const handleForgotPassword = () => {
  ElMessage.info("请通过邮箱找回密码");
};

onMounted(() => {
  const savedAccount = localStorage.getItem("savedAccount");
  if (savedAccount) {
    try {
      const account = JSON.parse(savedAccount);
      Object.assign(passwordLogin, account);
    } catch (e) {
      localStorage.removeItem("savedAccount");
    }
  }
});
</script>

<style scoped lang="scss">
@use "sass:color";

// 变量定义
$primary-color: #0089ee;
$text-main: #303133;
$text-secondary: #909399;
$bg-input: #f5f7fa;
$border-radius: 8px;

.login-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.login-form-container {
  width: 100%;
  max-width: 420px;
  min-width: 0;
  box-sizing: border-box;
  padding: 30px 40px;
  background-color: #fff;
}

/* --- 顶部 Tab 样式 --- */
.login-header {
  margin-bottom: clamp(24px, 4vh, 35px);
}

.custom-tabs {
  display: flex;
  justify-content: center;
  gap: clamp(24px, 6vw, 40px);
  flex-wrap: wrap;
  row-gap: 10px;
  position: relative;
}

.tab-item {
  position: relative;
  line-height: 1.25;
  font-size: 16px;
  color: $text-secondary;
  cursor: pointer;
  padding-bottom: 8px;
  white-space: nowrap;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    color: $primary-color;
  }

  &.active {
    color: $text-main;
    font-size: 18px;
    font-weight: 600;

    .active-line {
      width: 24px;
      opacity: 1;
      transform: translateX(-50%) scaleX(1);
    }
  }

  .active-line {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: 20px;
    height: 3px;
    background: $primary-color;
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
  }
}

/* --- 表单样式覆盖 --- */
.custom-form {
  :deep(.el-form-item) {
    margin-bottom: 24px;
  }
}

// 输入框样式定制
.custom-input {
  :deep(.el-input__wrapper) {
    padding: 1px 15px;
    background-color: $bg-input;
    box-shadow: none !important;
    border: 1px solid transparent;
    border-radius: $border-radius;
    transition: all 0.3s;
    height: 46px;

    &.is-focus {
      background-color: #fff;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.1) !important;
    }
    
    &:hover:not(.is-focus) {
      background-color: color.adjust($bg-input, $lightness: -2%);
    }
  }
  
  :deep(.el-input__inner) {
    height: 46px;
    font-size: 15px;
  }

  :deep(.el-input__prefix-inner) {
    font-size: 18px;
    color: $text-secondary;
  }
}

// 验证码输入框特殊处理
.code-input {
  :deep(.el-input-group__append) {
    background-color: transparent;
    box-shadow: none;
    padding: 0 10px;
  }
  
  .code-btn {
    margin-left: 5px;
    color: $primary-color;
    font-weight: 500;
    
    &:hover {
      opacity: 0.8;
    }
    
    &.is-disabled {
      color: $text-secondary;
    }
  }
}

/* --- 底部选项 --- */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -5px;
  margin-bottom: 30px;

  :deep(.el-checkbox__label) {
    color: $text-secondary;
  }

  .forgot-link {
    font-size: 14px;
    color: $text-secondary;
    
    &:hover {
      color: $primary-color;
    }
  }
}

/* --- 按钮组 --- */
.form-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .action-btn {
    width: 100%;
    height: clamp(44px, 6.6vh, 48px);
    font-size: 16px;
    margin-left: 0;
    border-radius: 24px;
    letter-spacing: 1px;
  }

  .login-btn {
    background: linear-gradient(135deg, #0089ee 0%, #0066cc 100%);
    border: none;
    box-shadow: 0 6px 16px rgba(0, 137, 238, 0.25);
    font-weight: 600;
    transition: transform 0.1s;

    &:active {
      transform: scale(0.98);
    }
  }

  .register-btn {
    background-color: #fff;
    border: 1px solid #dcdfe6;
    color: $text-main;

    &:hover {
      border-color: $primary-color;
      color: $primary-color;
      background-color: #f0f9ff;
    }
  }
}

/* --- 多账号选择卡片样式 --- */
.account-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 5px;

  .account-card {
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #fff;

    &:hover {
      border-color: rgba($primary-color, 0.5);
      background-color: #f9fcff;
    }

    &.is-selected {
      border-color: $primary-color;
      background-color: #f0f9ff;
      box-shadow: 0 2px 12px rgba($primary-color, 0.1);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .user-name {
        font-size: 16px;
        font-weight: 600;
        color: $text-main;
      }

      .check-icon {
        color: $primary-color;
        font-size: 18px;
        font-weight: bold;
      }
    }

    .card-info {
      p {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 4px 0;
        font-size: 14px;
        color: $text-secondary;
        
        .el-icon {
          font-size: 15px;
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
  padding-top: 10px;
}

@media (max-height: 680px) {
  .login-header {
    margin-bottom: 22px;
  }

  .custom-form {
    :deep(.el-form-item) {
      margin-bottom: 18px;
    }
  }

  .form-options {
    margin-bottom: 22px;
  }

  .form-buttons {
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .login-form-container {
    padding: 18px 14px 16px;
  }

  .custom-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 4px;
    border-radius: 14px;
    background: #f1f6fb;
  }

  .tab-item {
    display: flex;
    justify-content: center;
    min-height: 38px;
    padding: 9px 6px 8px;
    border-radius: 11px;
    font-size: 14px;

    &.active {
      background: #ffffff;
      color: #0077d9;
      font-size: 14px;
      font-weight: 800;
      box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
    }

    .active-line {
      display: none;
    }
  }

  .login-header {
    margin-bottom: 16px;
  }

  .custom-form {
    :deep(.el-form-item) {
      margin-bottom: 14px;
    }
  }

  .custom-input {
    :deep(.el-input__wrapper),
    :deep(.el-input__inner) {
      height: 44px;
    }

    :deep(.el-input__wrapper) {
      border: 1px solid #e5edf5;
      border-radius: 12px;
      background: #f8fafc;
    }
  }

  .form-options {
    margin-top: 0;
    margin-bottom: 18px;
  }

  .form-buttons {
    gap: 10px;

    .action-btn {
      height: 46px;
      font-size: 15px;
      border-radius: 14px;
    }

    .login-btn {
      box-shadow: 0 10px 20px rgba(0, 137, 238, 0.2);
    }
  }
}
</style>
